import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import { useForklifts } from '@/hooks/useForklifts'
import { useBucketDaySummary, useBucketRange } from '@/hooks/useBuckets'
import { useBgImage } from '@/hooks/useBgImage'
import { usePoleMap } from '@/hooks/usePoleMap'
import type { PreviewPath } from '@/components/maps/TrajectoryCanvas'
import type { FrontendLog } from '@/types/frontendLog'
import type { PositionHistory, PositionPoint } from '@/types/forklift'
import type { PoleSnapshot } from '@/types/pole'
import { decodePoleNs } from '@/types/pole'

const SLOT_MS = 30 * 60 * 1000
const SLOTS_PER_DAY = 48
import {
  parseTimeParam,
  formatTimeForUrl,
  parseDateParam,
  formatDateForUrl,
} from '@/common/dateUrlParams'

export function useForkliftDetail() {
  const { forkId } = useParams<{ forkId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const timestampParam = searchParams.get('timestamp')
  const dateParam = parseDateParam(searchParams.get('date'))
  const startParam = searchParams.get('start')
  const endParam = searchParams.get('end')

  // URLのパラメータから日付を決定: date > timestamp > 今日
  const [selectedDate, setSelectedDate] = useState(() => {
    if (dateParam) return dateParam
    if (timestampParam) return format(new Date(timestampParam), 'yyyy-MM-dd')
    return format(new Date(), 'yyyy-MM-dd')
  })

  // 軌跡表示用の範囲（Date型で管理）
  const [trajectoryStart, setTrajectoryStart] = useState(() => {
    const dateStr =
      dateParam ||
      (timestampParam
        ? format(new Date(timestampParam), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'))
    const fromUrl = parseTimeParam(startParam, dateStr)
    if (fromUrl) {
      // startのみ指定の場合は前後30分を検索範囲とする
      if (!endParam) return new Date(fromUrl.getTime() - 30 * 60 * 1000)
      return fromUrl
    }
    const today = new Date(dateStr)
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0
    )
  })

  const [trajectoryEnd, setTrajectoryEnd] = useState(() => {
    const dateStr =
      dateParam ||
      (timestampParam
        ? format(new Date(timestampParam), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'))
    const fromUrl = parseTimeParam(endParam, dateStr)
    if (fromUrl) return fromUrl
    if (startParam) {
      const startFromUrl = parseTimeParam(startParam, dateStr)
      // startのみ指定の場合は前後30分を検索範囲とする
      if (startFromUrl) return new Date(startFromUrl.getTime() + 30 * 60 * 1000)
    }
    return new Date()
  })

  // startのみ指定時の元のstart時刻（自動選択の基準点）
  const startOnlyTimeRef = useRef<Date | null>(
    startParam && !endParam
      ? parseTimeParam(
          startParam,
          dateParam ||
            (timestampParam
              ? format(new Date(timestampParam), 'yyyy-MM-dd')
              : format(new Date(), 'yyyy-MM-dd'))
        )
      : null
  )

  // URL同期用
  const urlInitializedRef = useRef(!!startParam || !!endParam)

  // URLパラメータを更新する関数
  const updateUrlParams = useCallback(
    (date: string, start: Date, end: Date) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('date', formatDateForUrl(date))
      newParams.set('start', formatTimeForUrl(start))
      newParams.set('end', formatTimeForUrl(end))
      newParams.delete('timestamp')
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // 現在の再生位置
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState<Date | null>(
    null
  )

  // ログクリック時のシーク先時刻
  const [targetSeekTime, setTargetSeekTime] = useState<Date | null>(() => {
    if (timestampParam) return new Date(timestampParam)
    return null
  })

  // ログ詳細モーダル用
  const [selectedLog, setSelectedLog] = useState<FrontendLog | null>(null)

  // シナリオのプレビューパス
  const [previewPaths, setPreviewPaths] = useState<PreviewPath[]>([])

  // 現在選択中のシナリオインデックス
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState<number>(-1)

  // 選択中のログ行ID（ハイライト用）
  const [selectedLogId, setSelectedLogId] = useState<string | undefined>(
    undefined
  )

  // ログ行を手動クリックした直後かどうか（自動選択の上書きを防止）
  const manualSelectRef = useRef(false)

  // 選択日の最小・最大時刻
  const trajectoryBounds = useMemo(() => {
    const minTime = new Date(`${selectedDate}T00:00:00`)
    const maxTime = new Date(`${selectedDate}T23:59:59`)
    return { minTime, maxTime }
  }, [selectedDate])

  // フォークリフト一覧から該当フォークリフトを検索（site_id取得用）
  const { data: forkliftsData } = useForklifts({ limit: 1000 })
  const forkliftInfo = useMemo(() => {
    return forkliftsData?.data?.find((f) => f.fork_id === forkId)
  }, [forkliftsData, forkId])

  // 背景画像を取得（再生位置の時点の画像を表示、なければ選択日の末尾）
  // APIリクエスト削減のため1分単位に丸める。粗く丸めるとマップ切替直後の
  // 再生位置で切替前の画像（別マップの canvasParams）が選ばれ、軌跡と
  // 背景がズレるため、丸め幅は最小限にする。同じ imageId なら useBgImage が
  // 再デコードしないので、画像自体の再取得は発生しない。
  const bgImageTimestamp = useMemo(() => {
    if (currentPlaybackTime) {
      const t = new Date(currentPlaybackTime)
      t.setSeconds(0, 0)
      return t.toISOString()
    }
    return new Date(`${selectedDate}T23:59:59.999Z`).toISOString()
  }, [currentPlaybackTime, selectedDate])
  const { bgImage, canvasParams: bgImageCanvasParams } = useBgImage(
    forkliftInfo?.site_id,
    bgImageTimestamp,
    forkId
  )

  // ポールマップ（既知ポール位置）を取得（背景画像と同じく再生位置の時点のものを表示）
  const { mapPoles } = usePoleMap(forkId, bgImageTimestamp)

  // 全日のバケット軽量メタを取得（ヒストグラム + 初期範囲決定用）
  const { data: daySummary } = useBucketDaySummary(forkId, selectedDate)

  // 既存の TimeRangeSlider が期待する形に変換（48スロット ヒストグラム）
  // 「移動距離」と「ログ件数」をそれぞれ正規化し、max を取って活動度として表示する。
  // /state はフォーク電源 ON 中常時流れるので position_total だと静止中もバーが立つため避ける。
  const allDayPositions = useMemo(() => {
    if (!daySummary) return undefined
    const distanceBins = new Array<number>(SLOTS_PER_DAY).fill(0)
    const logBins = new Array<number>(SLOTS_PER_DAY).fill(0)
    const jstStartMs = new Date(`${selectedDate}T00:00:00+09:00`).getTime()
    for (const b of daySummary.buckets) {
      const utcMs = new Date(`${b.bucket_key}:00.000Z`).getTime()
      const idx = Math.floor((utcMs - jstStartMs) / SLOT_MS)
      if (idx >= 0 && idx < SLOTS_PER_DAY) {
        distanceBins[idx] += b.distance_m ?? 0
        logBins[idx] += b.log_total ?? 0
      }
    }
    const maxDistance = distanceBins.reduce((m, v) => (v > m ? v : m), 0)
    const maxLog = logBins.reduce((m, v) => (v > m ? v : m), 0)
    const bins = distanceBins.map((d, i) => {
      const dNorm = maxDistance > 0 ? d / maxDistance : 0
      const lNorm = maxLog > 0 ? logBins[i] / maxLog : 0
      return Math.max(dNorm, lNorm) * 100
    })
    const maxCount = bins.reduce((m, v) => (v > m ? v : m), 0)
    return {
      fork_id: forkId!,
      positions: [],
      bounds: null,
      histogram: { bins, maxCount, binSize: 'hour' as const },
    }
  }, [daySummary, selectedDate, forkId])

  // 初期範囲設定済みフラグ（日付ごとに管理）
  const initializedDateRef = useRef<string | null>(null)
  const timestampProcessedRef = useRef(false)
  // 範囲が確定したかどうか（データフェッチのゲートに使用）
  // 初期範囲で即座にフェッチ開始し、ヒストグラム到着後に範囲を更新
  const [rangeReady, setRangeReady] = useState(true)

  // ヒストグラムデータから最初のデータがある時刻を見つけて初期範囲を設定
  useEffect(() => {
    if (
      allDayPositions?.histogram &&
      initializedDateRef.current !== selectedDate
    ) {
      if (urlInitializedRef.current) {
        const startInBounds =
          trajectoryStart >= trajectoryBounds.minTime &&
          trajectoryStart <= trajectoryBounds.maxTime
        const endInBounds =
          trajectoryEnd >= trajectoryBounds.minTime &&
          trajectoryEnd <= trajectoryBounds.maxTime
        // URL で start/end が明示されている場合はユーザーの意図として尊重する。
        // 当日分はまだ Rollup されておらずヒストグラムが空でも、bucket-range 側で
        // recent-messages から合成して表示できる。
        if (startInBounds && endInBounds && trajectoryStart < trajectoryEnd) {
          initializedDateRef.current = selectedDate
          updateUrlParams(selectedDate, trajectoryStart, trajectoryEnd)
          urlInitializedRef.current = false
          setRangeReady(true)
          return
        }
        urlInitializedRef.current = false
      }

      if (timestampParam && !timestampProcessedRef.current) {
        const logTime = new Date(timestampParam)
        const halfRangeMs = 30 * 60 * 1000
        let newStart = new Date(logTime.getTime() - halfRangeMs)
        let newEnd = new Date(logTime.getTime() + halfRangeMs)

        if (newStart < trajectoryBounds.minTime) {
          newStart = trajectoryBounds.minTime
          newEnd = new Date(newStart.getTime() + halfRangeMs * 2)
        }
        if (newEnd > trajectoryBounds.maxTime) {
          newEnd = trajectoryBounds.maxTime
          newStart = new Date(newEnd.getTime() - halfRangeMs * 2)
          if (newStart < trajectoryBounds.minTime) {
            newStart = trajectoryBounds.minTime
          }
        }

        setTrajectoryStart(newStart)
        setTrajectoryEnd(newEnd)
        initializedDateRef.current = selectedDate
        timestampProcessedRef.current = true
        updateUrlParams(selectedDate, newStart, newEnd)
        setRangeReady(true)
        return
      }

      const bins = allDayPositions.histogram?.bins ?? []
      let lastSlotWithData = -1
      for (let i = bins.length - 1; i >= 0; i--) {
        if (bins[i] > 0) {
          lastSlotWithData = i
          break
        }
      }

      const date = new Date(selectedDate)
      const baseMs = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0,
        0,
        0
      ).getTime()
      let startTime: Date
      let endTime: Date
      if (lastSlotWithData >= 0) {
        // 48スロット時は 30分単位、24時は1時間単位のスロットインデックスを想定
        const slotMs = bins.length >= SLOTS_PER_DAY ? SLOT_MS : 60 * 60 * 1000
        startTime = new Date(baseMs + lastSlotWithData * slotMs)
        endTime = new Date(startTime.getTime() + 60 * 60 * 1000)
      } else {
        // データなし: 既定範囲（00:00〜01:00）。rangeReady は立てて API は叩かせる
        startTime = new Date(baseMs)
        endTime = new Date(baseMs + 60 * 60 * 1000)
      }
      // maxTime を超えないようクランプ
      if (endTime > trajectoryBounds.maxTime) {
        endTime = trajectoryBounds.maxTime
        if (startTime > endTime) startTime = trajectoryBounds.minTime
      }

      setTrajectoryStart(startTime)
      setTrajectoryEnd(endTime)
      initializedDateRef.current = selectedDate
      updateUrlParams(selectedDate, startTime, endTime)
      setRangeReady(true)
    }
  }, [
    allDayPositions,
    selectedDate,
    timestampParam,
    trajectoryBounds.minTime,
    trajectoryBounds.maxTime,
    updateUrlParams,
    trajectoryStart,
    trajectoryEnd,
  ])

  // 30分境界に丸めた範囲（サーバ側でも丸めるが、キャッシュキー安定化のため client でも揃える）
  const snappedRange = useMemo(() => {
    const startMs = Math.floor(trajectoryStart.getTime() / SLOT_MS) * SLOT_MS
    const endMs = Math.ceil(trajectoryEnd.getTime() / SLOT_MS) * SLOT_MS
    return {
      start: new Date(startMs).toISOString(),
      end: new Date(endMs).toISOString(),
    }
  }, [trajectoryStart, trajectoryEnd])

  // 選択範囲のバケットを取得（軌跡 + ログを 1 回のリクエストで取得）
  const {
    data: rangeData,
    isLoading: loadingRange,
    isFetching: fetchingRange,
  } = useBucketRange(forkId, snappedRange.start, snappedRange.end, {
    enabled: rangeReady,
  })

  const loadingPositions = loadingRange
  const fetchingPositions = fetchingRange
  const loadingLogs = loadingRange
  const fetchingLogs = fetchingRange

  // 軌跡用 PositionHistory に変換
  const positions: PositionHistory | undefined = useMemo(() => {
    if (!rangeData) return undefined
    const allPositions: PositionPoint[] = rangeData.buckets.flatMap(
      (b) => b.positions
    )
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity
    for (const p of allPositions) {
      if (p.x < minX) minX = p.x
      if (p.x > maxX) maxX = p.x
      if (p.y < minY) minY = p.y
      if (p.y > maxY) maxY = p.y
    }
    return {
      fork_id: forkId!,
      positions: allPositions,
      bounds: allPositions.length > 0 ? { minX, maxX, minY, maxY } : null,
    }
  }, [rangeData, forkId])

  // 検出ポールスナップショットに変換（ns 短縮コードを復元し時刻昇順に整列）
  const poleSnapshots: PoleSnapshot[] = useMemo(() => {
    if (!rangeData) return []
    return rangeData.buckets
      .flatMap((b) => b.pole_snapshots ?? [])
      .map((s) => ({
        ...s,
        poles: s.poles.map((p) => ({ ...p, ns: decodePoleNs(p.ns) })),
      }))
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
  }, [rangeData])

  // ログ用 FrontendLogsResponse に変換（新しい順 = 降順）
  // 後段の seek-closest-log や autoSelect は降順前提のロジックで書かれている
  const logsDataRaw = useMemo(() => {
    if (!rangeData) return undefined
    const allLogs: FrontendLog[] = rangeData.buckets
      .flatMap((b) => b.logs)
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
    return {
      data: allLogs,
      pagination: { page: 1, limit: allLogs.length, total: allLogs.length },
      summary: {
        level_counts: { info: 0, warn: 0, error: 0, debug: 0 },
        time_range: { start: rangeData.start, end: rangeData.end },
      },
    }
  }, [rangeData])

  // robot_idがforkIdと一致し、検索時間範囲内のログのみをフィルタリング
  const logsData = useMemo(() => {
    if (!logsDataRaw?.data || !forkId) return logsDataRaw
    const startMs = trajectoryStart.getTime()
    const endMs = trajectoryEnd.getTime()
    const filteredData = logsDataRaw.data.filter((log) => {
      if (log.robot_id !== forkId) return false
      const logMs = new Date(log.timestamp).getTime()
      return logMs >= startMs && logMs <= endMs
    })
    return { ...logsDataRaw, data: filteredData }
  }, [logsDataRaw, forkId, trajectoryStart, trajectoryEnd])

  // シナリオ実行開始のログからマーカーを生成
  const scenarioMarkers = useMemo(() => {
    if (!logsData?.data) return []
    return logsData.data
      .filter(
        (log) =>
          log.action === 'scenario_execute' &&
          log.message === 'シナリオ実行開始'
      )
      .map((log) => ({
        time: new Date(log.timestamp),
        label: log.message || 'シナリオ実行',
        color: '#10b981',
      }))
  }, [logsData])

  // シナリオ実行開始のログリスト（ナビゲーション用）
  const scenarioLogs = useMemo(() => {
    if (!logsData?.data) return []
    return logsData.data.filter(
      (log) =>
        log.action === 'scenario_execute' && log.message === 'シナリオ実行開始'
    )
  }, [logsData])

  // start/end指定時、ログ読み込み後にstart時刻前後3分以内のエラー/警告ログを自動選択
  const autoSelectProcessedRef = useRef(false)
  useEffect(() => {
    if (autoSelectProcessedRef.current) return
    if (!startParam && !timestampParam) return
    if (!logsData?.data || logsData.data.length === 0) return

    // 基準時刻: startのみ → 元のstart時刻、start+end → 範囲の中間点、timestamp → timestamp
    let baseTimeMs: number | null = null
    if (startOnlyTimeRef.current) {
      baseTimeMs = startOnlyTimeRef.current.getTime()
    } else if (startParam && endParam) {
      baseTimeMs = (trajectoryStart.getTime() + trajectoryEnd.getTime()) / 2
    } else if (timestampParam) {
      baseTimeMs = new Date(timestampParam).getTime()
    }
    if (baseTimeMs === null) return

    autoSelectProcessedRef.current = true

    const logs = logsData.data
    const thresholdMs = 3 * 60 * 1000 // 前後3分

    const isWithinThreshold = (log: FrontendLog) =>
      Math.abs(new Date(log.timestamp).getTime() - baseTimeMs!) <= thresholdMs

    // 優先度1: 基準時刻前後3分以内のエラーログ
    const errorLog = logs.find(
      (log) => log.level === 'error' && isWithinThreshold(log)
    )
    if (errorLog) {
      setSelectedLogId(errorLog.log_id)
      setTargetSeekTime(new Date(errorLog.timestamp))
      return
    }

    // 優先度2: 基準時刻前後3分以内の警告ログ
    const warnLog = logs.find(
      (log) => log.level === 'warn' && isWithinThreshold(log)
    )
    if (warnLog) {
      setSelectedLogId(warnLog.log_id)
      setTargetSeekTime(new Date(warnLog.timestamp))
      return
    }

    // 優先度3: 基準時刻前後3分以内のシナリオ終了ログ
    const scenarioEndLog = logs.find(
      (log) =>
        log.action === 'scenario_complete' &&
        log.message === 'シナリオ終了' &&
        isWithinThreshold(log)
    )
    if (scenarioEndLog) {
      setSelectedLogId(scenarioEndLog.log_id)
      setTargetSeekTime(new Date(scenarioEndLog.timestamp))
      return
    }

    // 優先度4: 基準時刻前後3分以内のシナリオ実行開始ログ
    const scenarioStartLog = logs.find(
      (log) =>
        log.action === 'scenario_execute' &&
        log.message === 'シナリオ実行開始' &&
        isWithinThreshold(log)
    )
    if (scenarioStartLog) {
      setSelectedLogId(scenarioStartLog.log_id)
      setTargetSeekTime(new Date(scenarioStartLog.timestamp))
    }
  }, [logsData])

  // シークバー移動時に再生時刻に最も近いログをハイライト
  useEffect(() => {
    if (manualSelectRef.current) {
      manualSelectRef.current = false
      return
    }
    if (!currentPlaybackTime || !logsData?.data || logsData.data.length === 0)
      return
    const playbackMs = currentPlaybackTime.getTime()

    // 再生時刻以前で最も近いログを探す（ログは降順）
    // 同一秒に複数ログがある場合は一番古いもの（降順の最後）を選択
    let closest: FrontendLog | undefined
    let closestSec = -1
    for (const log of logsData.data) {
      const logMs = new Date(log.timestamp).getTime()
      if (logMs <= playbackMs) {
        const logSec = Math.floor(logMs / 1000)
        if (closestSec < 0) {
          closestSec = logSec
        }
        if (logSec === closestSec) {
          closest = log
        } else {
          break
        }
      }
    }

    if (closest) {
      setSelectedLogId(closest.log_id)
    }
  }, [currentPlaybackTime, logsData])

  // ログの時刻が現在の軌跡範囲内かチェック
  const isWithinCurrentRange = (logTime: Date) => {
    return (
      logTime.getTime() >= trajectoryStart.getTime() &&
      logTime.getTime() <= trajectoryEnd.getTime()
    )
  }

  // シナリオマーカークリック時のジャンプハンドラ（シーク位置のみ変更、時間範囲は変更しない）
  const handleMarkerClick = useCallback((time: Date) => {
    setTargetSeekTime(time)
  }, [])

  // シナリオナビゲーション（シーク位置とプレビューパスのみ変更、時間範囲は変更しない）
  const navigateToScenario = useCallback(
    (index: number) => {
      if (index < 0 || index >= scenarioLogs.length) return
      const log = scenarioLogs[index]
      setCurrentScenarioIndex(index)
      setSelectedLogId(log.log_id)

      if (
        log.context?.trajectories &&
        Array.isArray(log.context.trajectories)
      ) {
        setPreviewPaths(log.context.trajectories as PreviewPath[])
      } else {
        setPreviewPaths([])
      }

      setTargetSeekTime(new Date(log.timestamp))
    },
    [scenarioLogs]
  )

  const handlePrevScenario = useCallback(() => {
    if (currentScenarioIndex > 0) {
      navigateToScenario(currentScenarioIndex - 1)
    }
  }, [currentScenarioIndex, navigateToScenario])

  const handleNextScenario = useCallback(() => {
    if (currentScenarioIndex < 0) {
      navigateToScenario(0)
    } else if (currentScenarioIndex < scenarioLogs.length - 1) {
      navigateToScenario(currentScenarioIndex + 1)
    }
  }, [currentScenarioIndex, scenarioLogs.length, navigateToScenario])

  // 軌跡範囲の変更ハンドラ
  const handleTrajectoryRangeChange = useCallback(
    (start: Date, end: Date) => {
      setTrajectoryStart(start)
      setTrajectoryEnd(end)
      updateUrlParams(selectedDate, start, end)
    },
    [selectedDate, updateUrlParams]
  )

  // ログ行クリック時のジャンプハンドラ
  const handleLogRowClick = useCallback(
    (log: FrontendLog) => {
      const logTime = new Date(log.timestamp)
      const seekTime = logTime
      manualSelectRef.current = true
      setSelectedLogId(log.log_id)

      // シナリオ実行開始の場合（シーク位置とプレビューパスのみ変更、時間範囲は変更しない）
      if (
        log.action === 'scenario_execute' &&
        log.message === 'シナリオ実行開始'
      ) {
        const scenarioIndex = scenarioLogs.findIndex(
          (l) => l.log_id === log.log_id
        )
        setCurrentScenarioIndex(scenarioIndex)

        if (
          log.context?.trajectories &&
          Array.isArray(log.context.trajectories)
        ) {
          setPreviewPaths(log.context.trajectories as PreviewPath[])
        } else {
          setPreviewPaths([])
        }
        setTargetSeekTime(seekTime)
        return
      }

      // シナリオ実行開始以外はプレビューパスをクリア
      setPreviewPaths([])
      setCurrentScenarioIndex(-1)

      // シナリオ終了・中断の場合（シーク位置のみ変更、時間範囲は変更しない）
      if (
        (log.action === 'scenario_complete' &&
          log.message === 'シナリオ終了') ||
        (log.action === 'scenario_cancel' && log.message === 'シナリオ中断')
      ) {
        setTargetSeekTime(seekTime)
        return
      }

      // その他のログ: 範囲内なら再検索せずにマーカー表示のみ
      if (isWithinCurrentRange(logTime)) {
        setTargetSeekTime(seekTime)
        return
      }

      // 範囲外のログは前後30分の範囲を設定
      const halfRangeMs = 30 * 60 * 1000
      let newStart = new Date(logTime.getTime() - halfRangeMs)
      let newEnd = new Date(logTime.getTime() + halfRangeMs)

      if (newStart < trajectoryBounds.minTime) {
        newStart = trajectoryBounds.minTime
        newEnd = new Date(newStart.getTime() + halfRangeMs * 2)
      }
      if (newEnd > trajectoryBounds.maxTime) {
        newEnd = trajectoryBounds.maxTime
        newStart = new Date(newEnd.getTime() - halfRangeMs * 2)
        if (newStart < trajectoryBounds.minTime) {
          newStart = trajectoryBounds.minTime
        }
      }

      setTrajectoryStart(newStart)
      setTrajectoryEnd(newEnd)
      setTargetSeekTime(seekTime)
      updateUrlParams(selectedDate, newStart, newEnd)
    },
    [
      scenarioLogs,
      logsData,
      trajectoryBounds.minTime,
      trajectoryBounds.maxTime,
      trajectoryStart,
      trajectoryEnd,
      selectedDate,
      updateUrlParams,
    ]
  )

  // 日付変更ハンドラ
  const handleDateChange = useCallback(
    (newDate: string) => {
      setSelectedDate(newDate)
      initializedDateRef.current = null
      // 別日に切り替わった瞬間に旧 trajectoryEnd（前日や "now"）が残ってスライダーが範囲外を指すのを防ぐ
      const dayStart = new Date(`${newDate}T00:00:00`)
      const dayEndDefault = new Date(dayStart.getTime() + 60 * 60 * 1000)
      setTrajectoryStart(dayStart)
      setTrajectoryEnd(dayEndDefault)
      // 日付変更後は URL の start/end を捨てるので、初回の URL 同期分岐を無効化する
      urlInitializedRef.current = false
      timestampProcessedRef.current = false
      setRangeReady(false)
      setPreviewPaths([])
      setTargetSeekTime(null)
      setCurrentPlaybackTime(null)
      const newParams = new URLSearchParams(searchParams)
      newParams.set('date', formatDateForUrl(newDate))
      newParams.delete('start')
      newParams.delete('end')
      newParams.delete('timestamp')
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const isAnyFetching = fetchingPositions || fetchingLogs

  return {
    forkId,
    siteId: forkliftInfo?.site_id,
    selectedDate,
    trajectoryStart,
    trajectoryEnd,
    trajectoryBounds,
    currentPlaybackTime,
    setCurrentPlaybackTime,
    targetSeekTime,
    selectedLog,
    setSelectedLog,
    previewPaths,
    currentScenarioIndex,
    selectedLogId,
    positions,
    loadingPositions,
    fetchingPositions,
    logsData,
    loadingLogs,
    fetchingLogs,
    allDayPositions,
    scenarioMarkers,
    scenarioLogs,
    isAnyFetching,
    bgImage,
    bgImageCanvasParams,
    mapPoles,
    poleSnapshots,
    handleDateChange,
    handleTrajectoryRangeChange,
    handleMarkerClick,
    handleLogRowClick,
    handlePrevScenario,
    handleNextScenario,
  }
}
